/**
 * API endpoint definitions — const object for all backend routes.
 * Matches backend API contracts from docs/designs/backend-architecture.md
 *
 * Paths are relative to the client baseURL (set via REACT_APP_API_URL).
 * In production: REACT_APP_API_URL=http://host/api/v1
 * In tests:      baseURL is empty, so paths are used as-is.
 */

export const ENDPOINTS = {
  // ─── Todos ───────────────────────────────────────────────
  todos: {
    list: '/todos',
    create: '/todos',
    get: (id: string) => `/todos/${id}`,
    update: (id: string) => `/todos/${id}`,
    delete: (id: string) => `/todos/${id}`,
    complete: (id: string) => `/todos/${id}/complete`,
    scheduleComplete: (id: string) => `/todos/${id}/schedule-complete`,
    snooze: (id: string) => `/todos/${id}/snooze`,
    skip: (id: string) => `/todos/${id}/skip`,
    claim: (id: string) => `/todos/${id}/claim`,
    move: (id: string) => `/todos/${id}/move`,
    markBlocked: (id: string) => `/todos/${id}/mark-blocked`,
    unblock: (id: string) => `/todos/${id}/unblock`,
    smart: '/todos/smart',
    completions: '/todos/completions',
  updateCompletion: (id: string) => `/todos/completions/${id}`,
  },

  // ─── Calendar ────────────────────────────────────────────
  calendar: {
    events: '/calendar/events',
    sync: '/calendar/sync',
  },

  // ─── Household ───────────────────────────────────────────
  household: {
    get: '/household',
    members: '/household/members',
    invite: '/household/invite',
  },

  // ─── Plugins ─────────────────────────────────────────────
  plugins: {
    list: '/plugins',
    get: (id: string) => `/plugins/${id}`,
    enable: (id: string) => `/plugins/${id}/enable`,
    disable: (id: string) => `/plugins/${id}/disable`,
  },

  // ─── Notifications ───────────────────────────────────────
  notifications: {
    list: '/notifications',
    dismiss: (id: string) => `/notifications/${id}/dismiss`,
  },

  // ─── Maintenance Plugin ──────────────────────────────────
  maintenance: {
    appliances: '/maintenance/appliances',
    appliance: (id: string) => `/maintenance/appliances/${id}`,
    serviceRecords: (applianceId: string) =>
      `/maintenance/appliances/${applianceId}/records`,
  },

  // ─── Projects ────────────────────────────────────────────
  projects: {
    list: '/projects',
    create: '/projects',
    get: (id: string) => `/projects/${id}`,
    outlines: '/projects/outlines',
    update: (id: string) => `/projects/${id}`,
    delete: (id: string) => `/projects/${id}`,
    archive: (id: string) => `/projects/${id}/archive`,
    unarchive: (id: string) => `/projects/${id}/unarchive`,
    createTask: (id: string) => `/projects/${id}/todos`,
    addDep: (id: string) => `/projects/${id}/deps`,
    removeDep: (id: string, depId: string) => `/projects/${id}/deps/${depId}`,
  },

  // ─── Contacts ────────────────────────────────────────────
  contacts: {
    search: '/contacts/search',
    enabled: '/contacts/enabled',
    create: '/contacts',
    get: (id: string) => `/contacts/${id}`,
    update: (id: string) => `/contacts/${id}`,
    enable: (id: string) => `/contacts/${id}/enable`,
    disable: (id: string) => `/contacts/${id}/disable`,
    enhance: (id: string) => `/contacts/${id}/enhance`,
    updateRole: (id: string) => `/contacts/${id}/role`,
    suggestions: (id: string) => `/contacts/${id}/suggestions`,
  },

  // ─── Background Tasks ────────────────────────────────────
  tasks: {
    list: '/tasks',
    get: (id: string) => `/tasks/${id}`,
    cancel: (id: string) => `/tasks/${id}/cancel`,
  },

  // ─── Suggestions ─────────────────────────────────────────
  suggestions: {
    get: (id: string) => `/suggestions/${id}`,
    apply: (id: string) => `/suggestions/${id}/apply`,
    reject: (id: string) => `/suggestions/${id}/reject`,
  },

  // ─── Admin System Info ───────────────────────────────────
  admin: {
    systemInfo: '/admin/system-info',
  },

  // ─── Brief ───────────────────────────────────────────────
  brief: {
    today: '/brief/today',
  },

  // ─── WebSocket ───────────────────────────────────────────
  ws: {
    connect: '/ws',
  },
} as const;
